"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShipmentApiClient = void 0;
var HttpMethod;
(function (HttpMethod) {
    HttpMethod["Get"] = "GET";
    HttpMethod["Post"] = "POST";
    HttpMethod["Patch"] = "PATCH";
    HttpMethod["Delete"] = "DELETE";
})(HttpMethod || (HttpMethod = {}));
class ShipmentApiClient {
    constructor(_httpClient) {
        this._httpClient = _httpClient;
    }
    /** Gets paginated responses from requests to SAPI in batches. */
    static async *_getBatches(query, clientMethod) {
        var _a, _b;
        let hasMore = true;
        let skip = (_a = query.skip) !== null && _a !== void 0 ? _a : 0;
        const limit = (_b = query.batchSize) !== null && _b !== void 0 ? _b : 10;
        const { batchSize, ...rest } = query;
        while (hasMore) {
            const response = await clientMethod({
                ...rest,
                limit,
                skip,
            });
            hasMore = response.more;
            skip += response.items.length;
            yield response;
        }
    }
    async getShipments(shipmentQuery, relations) {
        const { data: shipmentsResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                ...shipmentQuery,
                relations,
            },
            url: '/shipments',
        });
        return shipmentsResult;
    }
    async getShipmentsByKeyset(shipmentQuery, relations) {
        const { data: shipmentsResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                ...shipmentQuery,
                relations,
            },
            url: '/shipments/byKeyset',
        });
        return shipmentsResult;
    }
    /**
     * Special Case bulk fetching for Completed, InNetwork shipments that are missing a delivery receipt.
     */
    async getShipmentsMissingDeliveryReceipt(query) {
        const { data: shipmentsResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                ...query,
                missingDeliveryReceipt: true,
            },
            url: '/shipments',
        });
        return shipmentsResult;
    }
    /** Gets shipments missing delivery receipt in batches. */
    async *getShipmentsMissingDeliveryReceiptBatched(query) {
        yield* ShipmentApiClient._getBatches(query, this.getShipmentsMissingDeliveryReceipt.bind(this));
    }
    async getShipment(shipmentId, withDeleted, relations) {
        const { data: shipmentResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                relations,
                withDeleted,
            },
            url: `/shipments/${shipmentId}`,
        });
        return shipmentResult;
    }
    async getShipmentByReferenceNumber(referenceNumber, withDeleted, relations) {
        const { data: shipmentResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                isReferenceNumber: true,
                relations,
                withDeleted,
            },
            url: `/shipments/${referenceNumber}`,
        });
        return shipmentResult;
    }
    async getShipmentByFirebaseKey(firebaseKey, withDeleted, relations) {
        const { data: shipmentResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                isFirebaseKey: true,
                relations,
                withDeleted,
            },
            url: `/shipments/${firebaseKey}`,
        });
        return shipmentResult;
    }
    async updateShipment(shipmentId, updateData, metadata) {
        const body = {
            ...updateData,
            updateShipmentMetadata: metadata,
        };
        const { data: shipmentResult } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Patch,
            url: `/shipments/${shipmentId}`,
        });
        return shipmentResult;
    }
    async updateShipments(body) {
        const { data: result } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Patch,
            url: `/shipments`,
        });
        return result;
    }
    async deleteShipment(shipmentId) {
        const { data: shipmentResult } = await this._httpClient.request({
            method: HttpMethod.Delete,
            url: `/shipments/${shipmentId}`,
        });
        return shipmentResult;
    }
    async createShipment(body) {
        const { data: shipmentResult } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Post,
            url: `/shipments`,
        });
        return shipmentResult;
    }
    async createShipmentDiff(data) {
        await this._httpClient.request({
            data,
            method: 'POST',
            url: '/shipments/diff',
        });
    }
    async resolveDiff(shipmentId) {
        await this._httpClient.request({
            method: 'POST',
            url: `/shipments/diff/${shipmentId}/resolve`,
        });
    }
    async createWaypointNote(body, shipmentId, waypointId) {
        const { data: waypointNoteResult } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Post,
            url: `/shipments/${shipmentId}/waypoints/${waypointId}/notes`,
        });
        return waypointNoteResult;
    }
    async createWaypointNoteByType(body, shipmentId, waypointType) {
        const { data: waypointNoteResult } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Post,
            url: `/shipments/${shipmentId}/notes?waypointType=${waypointType}`,
        });
        return waypointNoteResult;
    }
    async getWaypointNotesForShipment(shipmentId) {
        const { data: waypointNotesResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            url: `/shipments/${shipmentId}/notes`,
        });
        return waypointNotesResult;
    }
    /**
     * Returns the waypoint service window history records for the given query.
     */
    async getWaypointServiceWindowHistories(query) {
        const { data: response } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: query,
            url: `/waypoints/service-window-history`,
        });
        return response;
    }
    /**
     * Returns waypoint service window change reasons for the given query.
     */
    async getWaypointServiceWindowChangeReasons(query = {}) {
        const { data: response } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: query,
            url: `/waypoints/service-window-change-reasons`,
        });
        return response;
    }
    /**
     * Creates a new waypoint service window change reason.
     */
    async createWaypointServiceWindowChangeReason(data) {
        const { data: response } = await this._httpClient.request({
            data,
            method: HttpMethod.Post,
            url: `/waypoints/service-window-change-reasons`,
        });
        return response;
    }
    /**
     * Updates an existing waypoint service window change reason.
     */
    async updateWaypointServiceWindowChangeReason(id, data) {
        const { data: response } = await this._httpClient.request({
            data,
            method: HttpMethod.Patch,
            url: `/waypoints/service-window-change-reasons/${id}`,
        });
        return response;
    }
    async deleteFreightForShipment(shipmentId, freightId, isFirebaseKey) {
        const { data: freightResult } = await this._httpClient.request({
            method: HttpMethod.Delete,
            params: {
                isFirebaseKey,
            },
            url: `/shipments/${shipmentId}/freight/${freightId}`,
        });
        return freightResult;
    }
    async getTrackingForShipment(shipmentId) {
        const { data: trackingResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            url: `/shipments/${shipmentId}/tracking`,
        });
        return trackingResult;
    }
    async addTrackingForShipment(shipmentId, trackingBody) {
        const { data: trackingResult } = await this._httpClient.request({
            data: trackingBody,
            method: HttpMethod.Post,
            url: `/shipments/${shipmentId}/tracking`,
        });
        return trackingResult;
    }
    async getAppointmentForShipment(shipmentId, waypointId) {
        const { data: appointmentResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            url: `/shipments/${shipmentId}/waypoints/${waypointId}/appointment`,
        });
        return appointmentResult;
    }
    async updateAppointmentForShipment(shipmentId, waypointId, body) {
        const { data: appointmentResult } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Patch,
            url: `/shipments/${shipmentId}/waypoints/${waypointId}/appointment`,
        });
        return appointmentResult;
    }
    async createAppointmentForShipment(shipmentId, waypointId, body) {
        const { data: appointmentResult } = await this._httpClient.request({
            data: body,
            method: HttpMethod.Post,
            url: `/shipments/${shipmentId}/waypoints/${waypointId}/appointment`,
        });
        return appointmentResult;
    }
    async findShipmentDocuments(filter) {
        const { data: documentsResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                documentType: filter.where.type,
                limit: filter.limit,
                page: filter.page,
            },
            url: `/documents/shipment/${filter.where.shipmentId}`,
        });
        return documentsResult;
    }
    async getCountsPerShipmentBucket() {
        const { data: bucketsCount } = await this._httpClient.request({
            method: HttpMethod.Get,
            url: '/internal/countsPerShipmentBucket',
        });
        return bucketsCount;
    }
    async getShipmentsInBucket(bucket, filters, options) {
        const { data: shipmentsResult } = await this._httpClient.request({
            method: HttpMethod.Get,
            params: {
                ...filters,
                ...options,
            },
            url: `/internal/shipmentBucket/${bucket}`,
        });
        return shipmentsResult;
    }
}
exports.ShipmentApiClient = ShipmentApiClient;
//# sourceMappingURL=ShipmentApiClient.js.map